import React from 'react';
import Cookies from 'js-cookie';

/**
 * Delete-board confirmation modal — the destructive one. Shown before the
 * board is actually moved to "Recently Deleted".
 */
export const DeleteBoardModal = ({ t, show, onCancel, onConfirm }) => {
  if (!show) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '420px', padding: '32px', textAlign: 'center' }}
      >
        <h3 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '18px',
          marginBottom: '16px',
          color: 'var(--kahla-charcoal)',
        }}>
          {t.delete_board_title}
        </h3>
        <p style={{
          fontSize: '13px',
          color: 'var(--kahla-text-light)',
          lineHeight: '1.7',
          marginBottom: '28px',
        }}>
          {t.delete_board_warning}
        </p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            className="btn-secondary"
            style={{ padding: '10px 28px', fontSize: '13px' }}
            onClick={onCancel}
            data-testid="delete-board-cancel"
          >
            {t.cancel}
          </button>
          <button
            style={{
              padding: '10px 28px',
              fontSize: '13px',
              backgroundColor: 'var(--kahla-charcoal)',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              letterSpacing: '0.03em',
            }}
            onClick={onConfirm}
            data-testid="delete-board-confirm"
          >
            {t.delete_permanently}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Soft-delete confirmation — appears AFTER the board is moved to
 * Recently Deleted. Calm, reassuring tone. User dismisses to return.
 */
export const DeletedBoardConfirmation = ({ t, show, onDismiss }) => {
  if (!show) return null;
  return (
    <div
      className="modal-overlay"
      data-testid="board-deleted-confirmation-overlay"
      onClick={onDismiss}
    >
      <div
        className="modal-content"
        data-testid="board-deleted-confirmation-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '460px', padding: '44px 44px 36px', textAlign: 'center' }}
      >
        <p style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '11px',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--kahla-text-light)',
          margin: '0 0 18px',
          fontWeight: 300,
        }}>{t.studio_name || 'Maestra'}</p>
        <h3 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '22px',
          fontWeight: 400,
          lineHeight: 1.35,
          margin: '0 0 16px',
          color: 'var(--kahla-charcoal)',
        }}>
          {(t.delete_board_confirmation || '').split('\n\n')[0]}
        </h3>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: '16px',
          lineHeight: 1.65,
          margin: '0 0 32px',
          color: 'var(--kahla-text-light)',
        }}>
          {(t.delete_board_confirmation || '').split('\n\n')[1] || ''}
        </p>
        <button
          data-testid="board-deleted-confirmation-ok"
          onClick={onDismiss}
          style={{
            padding: '11px 32px',
            fontSize: '11px',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            backgroundColor: 'var(--kahla-charcoal)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {t.back_to_dashboard || 'Back to Dashboard'}
        </button>
      </div>
    </div>
  );
};

/**
 * "Extend / Reopen Board" modal — single days-select with a calm preview of
 * what stays preserved.
 */
export const ExtendBoardModal = ({
  t,
  show,
  isExpired,
  workspaceLang,
  extendDays,
  setExtendDays,
  extending,
  onClose,
  onConfirm,
}) => {
  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop" onClick={onClose}></div>
      <div className="modal-content fade-in" style={{ maxWidth: '400px' }}>
        <h2 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '22px',
          marginBottom: '8px',
          textAlign: 'center',
        }}>
          {isExpired ? t.reopen_board_title : t.extend_board_title}
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '13px',
          color: 'var(--kahla-text-light)',
          marginBottom: '24px',
        }}>
          {isExpired ? t.extend_reopen_subtitle : t.extend_subtitle}
        </p>

        <div className="form-group" style={{ marginBottom: '24px' }}>
          <label style={{ marginBottom: '8px', display: 'block' }}>
            {isExpired ? t.reopen_for : t.extend_by}
          </label>
          <select
            value={extendDays}
            onChange={(e) => setExtendDays(parseInt(e.target.value))}
            style={{ width: '100%', padding: '10px', fontSize: '14px' }}
            data-testid="extend-days-select"
          >
            <option value={7}>7 {t.days}</option>
            <option value={14}>14 {t.days}</option>
            <option value={30}>30 {t.days}</option>
            <option value={60}>60 {t.days}</option>
            <option value={90}>90 {t.days}</option>
          </select>
          <p style={{ fontSize: '11px', color: 'var(--kahla-text-light)', marginTop: '8px' }}>
            {t.new_expiration}: {new Date(Date.now() + extendDays * 24 * 60 * 60 * 1000).toLocaleDateString(workspaceLang === 'pl' ? 'pl-PL' : workspaceLang === 'de' ? 'de-DE' : 'en-GB')}
          </p>
        </div>

        <div style={{
          backgroundColor: 'var(--kahla-cream-light)',
          padding: '12px 16px',
          marginBottom: '20px',
          border: '1px solid var(--kahla-border)',
        }}>
          <p style={{ fontSize: '12px', color: 'var(--kahla-text)' }}>
            ✓ {t.same_share_link}<br />
            ✓ {t.all_images_preserved}<br />
            ✓ {t.all_reactions_preserved}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn-secondary"
            style={{ flex: 1 }}
            onClick={onClose}
            disabled={extending}
          >
            {t.cancel}
          </button>
          <button
            className="btn-primary"
            style={{ flex: 1 }}
            onClick={onConfirm}
            disabled={extending}
            data-testid="confirm-extend-btn"
          >
            {extending ? t.saving : (isExpired ? t.reopen_board_title : t.extend)}
          </button>
        </div>
      </div>
    </>
  );
};

/**
 * "Export ready" modal — copy-link / download row, shown after a PDF or ZIP
 * export request finishes. Authenticated blob fetch keeps the download
 * owner-bound.
 */
export const DownloadReadyModal = ({ downloadReady, copiedLink, onCopyLink, onClose, showToast }) => {
  if (!downloadReady) return null;
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.4)',
      }}
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'white', padding: '48px 40px', maxWidth: '440px', width: '90%',
        border: '1px solid var(--kahla-border)', textAlign: 'center',
      }}>
        <p style={{
          fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase',
          color: 'var(--kahla-text-light)', marginBottom: '16px',
        }}>
          Export ready
        </p>
        <h3 style={{
          fontFamily: 'Playfair Display, serif', fontSize: '22px',
          color: 'var(--kahla-charcoal)', marginBottom: '12px',
        }}>
          Your {downloadReady.type === 'ZIP' ? 'ZIP' : 'PDF'} is ready
        </h3>
        <p style={{
          fontSize: '13px', color: 'var(--kahla-text-light)', marginBottom: '32px',
          wordBreak: 'break-word',
        }}>
          {downloadReady.filename}
        </p>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
          <button
            onClick={() => onCopyLink(downloadReady.url)}
            data-testid="copy-link-btn"
            style={{
              flex: 1, padding: '14px 12px', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '0.04em', textAlign: 'center',
              backgroundColor: 'transparent',
              color: 'var(--kahla-charcoal)',
              border: '1px solid var(--kahla-charcoal)',
            }}
          >
            Copy link
          </button>

          {/* Download — fetch with auth (Bearer token) since the export
              endpoint is now owner-bound, then trigger a blob download. */}
          <button
            onClick={async () => {
              try {
                const r = await fetch(downloadReady.url, {
                  headers: { Authorization: `Bearer ${Cookies.get('kahla_token') || ''}` },
                });
                if (!r.ok) {
                  showToast('Download is no longer available — please re-export.');
                  return;
                }
                const blob = await r.blob();
                const objectUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = downloadReady.filename || 'export';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
              } catch (e) {
                console.error('Download failed', e);
                showToast('Download failed.');
              }
            }}
            data-testid="download-btn"
            style={{
              flex: 1, padding: '14px 12px', fontSize: '14px', fontWeight: '500',
              cursor: 'pointer', fontFamily: 'inherit',
              letterSpacing: '0.04em', textAlign: 'center',
              backgroundColor: 'var(--kahla-charcoal)',
              color: 'white', border: '1px solid var(--kahla-charcoal)',
            }}
          >
            Download {downloadReady.type === 'ZIP' ? 'ZIP' : 'PDF'}
          </button>
        </div>

        {copiedLink && (
          <p data-testid="copy-success-msg" style={{
            fontSize: '12px', color: '#4a7c59', margin: '0 0 12px', lineHeight: '1.4',
          }}>
            Link copied successfully
          </p>
        )}

        <button
          onClick={onClose}
          data-testid="export-modal-close-btn"
          style={{
            background: 'none', border: 'none', fontSize: '12px',
            color: 'var(--kahla-text-light)', cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};
